import BankAccountForm from '@/components/AccountConfig/BankAccountForm/index.vue';

const PERIODIC_OPERATION_TYPES = [
    'SOLICITUD_REEMBOLSO_PERIODICO',
    'SOLICITUD_APORTACION_PERIODICA',
    'SOLICITUD_SUSCRIPCION_PERIODICA'
];
const PERIODIC_OPERATION_TYPES_FILTER = `(${PERIODIC_OPERATION_TYPES.map(type => `'${type}'`).join(
    ','
)})`;

export default {
    name: 'BankAccounts',
    props: ['accountType', 'uuid', 'status'],

    components: { BankAccountForm },

    data() {
        return {
            editable: '',
            uuidAccount: '',
            bankAccounts: [],
            bankAccount: {},
            selectedBankAccount: {},
            selectedAccountDocuments: undefined,
            bankAccountBlockingOperations: [],
            originalBankAccount: {}
        };
    },

    mounted() {
        this.uuidAccount = this.uuid;
        this.getBankAccountBlockingOperations();
    },

    methods: {
        remove() {
            this.isLoading = true;
            this.$accountService
                .deleteAccountBankAccounts({}, this.uuidAccount, this.selectedBankAccount.uuid)
                .then(() => {
                    this.isLoading = false;
                    this.$emit('update');
                    this.$store.dispatch('resetDocumentItems');
                    this.loadBankAccountsData();
                    // close the modal
                    const idModal = `delete-account-${this.selectedBankAccount.account_number}`;
                    this.$bvModal.hide(idModal);
                })
                .catch(err => {
                    this.isLoading = false;

                    this.$bvToast.toast(`${err.message}`, {
                        title: 'Error',
                        toaster: 'b-toaster-top-center',
                        autoHideDelay: 10000,
                        variant: 'danger'
                    });

                    console.error(err);
                });
        },
        async setSelectedBankAccount(index) {
            this.selectedBankAccount = this.bankAccounts[index];
            this.originalBankAccount = this.selectedBankAccount;

            const operationsLoaded = await this.getBankAccountBlockingOperations(true);

            if (!operationsLoaded) return;

            this.$nextTick(() => {
                const idModal = `delete-account-${this.selectedBankAccount.account_number}`;
                this.$bvModal.show(idModal);
            });
        },
        isPeriodicOperation(operation) {
            return PERIODIC_OPERATION_TYPES.includes(operation.key_operation_type);
        },
        isActivePeriodicOperation(operation) {
            return this.isPeriodicOperation(operation) && operation.status !== 'CANCELLED';
        },
        isInFlightOperation(operation) {
            return operation.status !== 'FINALIZED' && operation.status !== 'CANCELLED';
        },
        blocksBankAccountDeletion(operation) {
            return this.isActivePeriodicOperation(operation) || this.isInFlightOperation(operation);
        },
        hasBlockingOperations() {
            return this.selectedBankAccountBlockingOperations.length > 0;
        },
        getBankAccountOperationsConfig(filter) {
            return {
                params: {
                    $filter: JSON.stringify(filter)
                }
            };
        },
        getActivePeriodicOperationsConfig() {
            return this.getBankAccountOperationsConfig({
                uuid_bank_account: [{ eq: this.selectedBankAccount.uuid }],
                status: [{ ne: 'CANCELLED' }],
                key_operation_type: [{ ina: PERIODIC_OPERATION_TYPES_FILTER }]
            });
        },
        getInFlightOperationsConfig() {
            return this.getBankAccountOperationsConfig({
                uuid_bank_account: [{ eq: this.selectedBankAccount.uuid }],
                status: [{ ne: 'FINALIZED' }, { ne: 'CANCELLED' }]
            });
        },
        mergeOperationsByUuid(operations) {
            return Object.values(
                operations.reduce((acc, operation) => {
                    acc[operation.uuid] = operation;
                    return acc;
                }, {})
            );
        },
        async getBankAccountBlockingOperations(showError = false) {
            if (!this.selectedBankAccount || !this.selectedBankAccount.uuid) {
                this.bankAccountBlockingOperations = [];
                return true;
            }

            try {
                const [periodicOperationsResponse, inFlightOperationsResponse] = await Promise.all([
                    this.$accountService.getAccountOperations(
                        this.getActivePeriodicOperationsConfig(),
                        this.uuidAccount
                    ),
                    this.$accountService.getAccountOperations(
                        this.getInFlightOperationsConfig(),
                        this.uuidAccount
                    )
                ]);
                const accountOperations = this.mergeOperationsByUuid([
                    ...(periodicOperationsResponse.data.related_operations || []),
                    ...(inFlightOperationsResponse.data.related_operations || [])
                ]);

                this.bankAccountBlockingOperations = accountOperations.filter(operation =>
                    this.blocksBankAccountDeletion(operation)
                );

                return true;
            } catch (error) {
                this.bankAccountBlockingOperations = [];

                if (showError) {
                    this.$bvToast.toast(`${error.message}`, {
                        title: 'Error',
                        toaster: 'b-toaster-top-center',
                        autoHideDelay: 10000,
                        variant: 'danger'
                    });
                }
                return false;
            }
        },
        edit(index) {
            this.editable = index;
        },

        update() {
            // Success notification
            this.$bvToast.toast('Se ha actualizado correctamente', {
                title: 'Actualizado',
                toaster: 'b-toaster-top-center',
                autoHideDelay: 10000,
                variant: 'success'
            });

            this.edit();
            this.loadBankAccountsData();
        },

        // Request collapse elements info
        async loadBankAccountsData() {
            // Get bank accounts
            this.$accountService
                .getAccountBankAccounts({}, this.uuidAccount)
                .then(response => {
                    this.bankAccounts = response.data.bank_accounts;

                    //TODO Borrar si no se utiliza finalmente (Preguntar a Aroa)
                    this.bankAccounts.map(bankAccount => {
                        bankAccount.account_number = bankAccount.iban_code.substr(
                            4,
                            bankAccount.iban_code.length
                        );
                    });
                })
                .catch(error => console.error(error));

            // Get account documents
            await this.loadAccountDocuments();
        },

        async loadAccountDocuments() {
            const account_documents = await this.$accountService.getAccountDocumentsValidations(
                {},
                this.uuid
            );
            this.selectedAccountDocuments = account_documents.data.accounts_documents.filter(
                doc => doc.key_subcategory === 'DOCUMENTO_ACREDITATIVO_TITULARIDAD_CC'
            );
        },

        pendingBankAccountDocuments(bankAccount) {
            const bankAccountDocumentUuid = bankAccount.uuid_document_ownership || null;

            if (!bankAccountDocumentUuid) {
                this.$emit('update');
                return;
            }

            return (
                this.selectedAccountDocuments &&
                this.selectedAccountDocuments.some(
                    doc =>
                        (doc.uuid === bankAccountDocumentUuid && !doc.title) ||
                        bankAccountDocumentUuid === null
                )
            );
        },

        getHolderName(bankAccount) {
            if (!bankAccount.holder) return '';

            const { name, lastname_1, lastname_2 } = bankAccount.holder;
            const fullName = [name, lastname_1, lastname_2].filter(Boolean).join(' ');

            return `${fullName} - ${bankAccount.holder.document_id}`;
        },

        // Navigates to the operation details
        goToOperation(uuid) {
            if (!uuid) return;
            try {
                const idModal = `delete-account-${this.selectedBankAccount.account_number}`;
                this.$bvModal.hide(idModal);
            } catch (e) {
                //console.error('Error closing modal:', e);
            }
            this.$router.push(`/account/operation/${uuid}`);
        }
    },
    computed: {
        selectedBankAccountBlockingOperations() {
            if (!this.selectedBankAccount || !this.selectedBankAccount.uuid) {
                return [];
            }

            return this.bankAccountBlockingOperations.filter(
                operation => operation.uuid_bank_account === this.selectedBankAccount.uuid
            );
        },
        canEdit() {
            return this.status === 'FINALIZED';
        }
    }
};
